import { Fragment, Node, Slice } from '@tiptap/pm/model';
import { isEmpty, isEqual } from 'lodash';
import moment from 'moment';
import Papa from 'papaparse';
import { Divider, Stack, Text } from 'shell/components';
import {
    AI_FEEDBACKS_URL,
    AI_GRADE_URL,
    AI_REGENERATE_URL,
    AI_RUBRICS_URL,
    AmexSvg,
    MastercardSvg,
    PRONOUNS_OPTIONS,
    VisaSvg,
} from 'utils/constants';
import { uploadFile } from 'utils/file';

export function splitArrayByCondition(arr, conditionCallback) {
    const result = {
        true: [],
        false: [],
    };
    arr.forEach((obj) => {
        const condition = conditionCallback(obj);
        result[condition].push(obj);
    });
    return result;
}

export const getUserPreferredFullName = (data) => {
    const { first_name = '', last_name = '', display_name = '', email = '' } = data || {};

    if (display_name) {
        return `${display_name || ''} ${last_name || ''}`.trim();
    }

    return `${first_name || ''} ${last_name || ''}`.trim() || email;
};

export const getUserPronoun = (userGender) => {
    const pronounOption = PRONOUNS_OPTIONS.find(({ key }) => key === userGender);
    if (!pronounOption) return '';
    return pronounOption.value;
};

export const getFileIconKind = (file) => {
    switch (file.type) {
        case 'application/pdf':
            return 'pdfType';
        case 'application/zip':
            return 'zipType';
        case 'application/msword':
        case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
            return 'docType';
        case 'image/gif':
            return 'gifType';
        case 'image/jpeg':
            return 'jpgType';
        case 'image/png':
            return 'pngType';
        case 'image/svg+xml':
            return 'svgType';
        case 'text/plain':
            return 'txtType';
        default:
            return 'attach';
    }
};

export const getS3FileURL = (data) => {
    if (!data) return;
    const { bucket, key, name } = data;
    if (!bucket || !key || !name) return '';
    return `https://${bucket}.s3.amazonaws.com/${key}/${encodeURIComponent(name)}`;
};

export const bytesToMB = (bytes) => {
    const MB = 1000000; // 1 MB = 1,000,000 bytes
    const mbValue = bytes / MB;
    return mbValue % 1 === 0 ? mbValue : parseFloat(mbValue.toFixed(2));
};

// si - true to use metric (SI) units, aka powers of 1000, false to use binary (IEC), aka powers of 1024
// dp - number of decimal places to display
export const getPrettyFileSize = (bytes, si = false, dp = 1) => {
    const thresh = si ? 1000 : 1024;

    if (Math.abs(bytes) < thresh) {
        return bytes + ' B';
    }

    const units = si
        ? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
        : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
    let u = -1;
    const r = 10 ** dp;

    do {
        bytes /= thresh;
        ++u;
    } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1);

    return bytes.toFixed(dp) + ' ' + units[u];
};

export const downloadFileFromURL = (url, name) => {
    const anchor = document.createElement('a');
    anchor.style.display = 'none';
    anchor.href = url;
    anchor.download = name;
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';

    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
};

export const getAbsoluteURL = (url) => {
    return ['http://', 'https://'].some((prefix) => url.startsWith(prefix)) ? url : `https://${url}`;
};

export const getPrettyTimestamp = (timestamp) => {
    const getAweekAgo = new Date().getTime() - 604800000;
    if (timestamp < getAweekAgo) return moment(timestamp).format('ll');
    else return moment(timestamp).startOf(new Date()).fromNow();
};

export const downloadCSV = (filename, csv) => {
    const unparsed = Papa.unparse(csv);
    const blob = new Blob([unparsed], { type: 'text/plain;charset=utf-8' });
    return downloadFileFromURL(URL.createObjectURL(blob), filename);
};

export const parseCSV = (file, expectedHeaders, message) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const text = reader.result;
            const parsed = Papa.parse(text);
            if (parsed.errors.length) {
                if (message) message.error('The CSV file could not be read. Please make sure that your file is valid.');
                reject(parsed.errors);
            }
            // remove last row if it is just an empty cell, this happens when there is a trailing newline
            const lastRow = parsed.data[parsed.data.length - 1];
            if (lastRow.length === 1 && lastRow[0] === '') {
                parsed.data = parsed.data.slice(0, parsed.data.length - 1);
            }
            const [header, ...rest] = parsed.data.map((row) => row.map((cell) => cell.trim()));
            if (message && !isEqual(header, expectedHeaders)) {
                message.error(
                    'We ran into an error reading your file. Please make sure the column headers match the CSV template.'
                );
            }
            resolve(rest);
        };
        if (file) reader.readAsText(file);
    });
};

export const isHTML = (string) => {
    return string.startsWith('<p') || string.startsWith('<ul') || string.startsWith('<ol') || string.startsWith('<li');
};

export const stripHTML = (string) => {
    if (!string) return '';
    if (!isHTML(string)) return string;

    // Create a temporary div element
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = string;

    // Flag to keep track of whether initial text has been processed
    let initialTextProcessed = false;

    // Function to process a single element
    function processElement(element) {
        if (element.nodeType === 3) {
            // If it's a text node, return its text content
            return element.textContent.trim();
        } else if (element.nodeType === 1) {
            // If it's an element node, process its children
            const tagName = element.tagName.toLowerCase();
            if (tagName === 'p' && !initialTextProcessed) {
                // Add two newline characters if it's the first <p> element
                initialTextProcessed = true;
                return element.textContent.trim() + '\n';
            } else if (tagName === 'ul') {
                // If it's an unordered list, prepend hyphens to each item
                const items = Array.from(element.children);
                return '\n' + items.map((li) => '- ' + processElement(li)).join('\n');
            } else if (tagName === 'ol') {
                // If it's an ordered list, prepend numbering to each item
                const items = Array.from(element.children);
                return items.map((li, index) => index + 1 + '. ' + processElement(li)).join('\n');
            } else {
                // Process other elements recursively
                return Array.from(element.childNodes).map(processElement).join('');
            }
        }
    }

    // Process the top-level elements in the temporary div
    return Array.from(tempDiv.childNodes)
        .map(processElement)
        .join('')
        .replace(/^\n+|\n+$/g, '');
};

export const toHTML = (string) => {
    if (isHTML(string)) return string;
    return `<p>${string.replaceAll('\n', '</p><p>')}</p>`;
};

// RTE handler for pasting
export const clipboardTextParser = (text, context) => {
    const blocks = text.replace().split(/(?:\r\n?|\n)/);
    const nodes = [];
    blocks.forEach((line) => {
        const nodeJSON = { type: 'paragraph' };
        if (line.length) {
            nodeJSON.content = [{ type: 'text', text: line }];
        }
        const node = Node.fromJSON(context.doc.type.schema, nodeJSON);
        nodes.push(node);
    });
    return Slice.maxOpen(Fragment.fromArray(nodes));
};

export const getTooltipComponent = (title, description) => {
    return (
        <Stack divider={<Divider />} spacing={1}>
            <Text color={'inherit'} variant={'p3'} weight={'semibold'}>
                {title}
            </Text>
            <Stack>
                <Text color={'inherit'} variant={'p3'}>
                    {description}
                </Text>
            </Stack>
        </Stack>
    );
};

export const getTooltipProps = (tooltip) => {
    const { component, maxWidth, placement, ...rest } = tooltip;
    return {
        title: component,
        maxWidth: maxWidth || '8.75rem',
        enterDelay: 300,
        placement: placement || 'bottom',
        ...rest,
    };
};

export const COURSE_ID_DICTIONARY = [
    '0',
    '1',
    '2',
    '3',
    '4',
    '5',
    '6',
    '7',
    '8',
    '9',
    'a',
    'b',
    'c',
    'd',
    'e',
    'f',
    'g',
    'h',
    'i',
    'j',
    'k',
    'l',
    'm',
    'n',
    'o',
    'p',
    'q',
    'r',
    's',
    't',
    'u',
    'v',
    'w',
    'x',
    'y',
    'z',
];

export const insertAtIndex = (arr, start, ...items) => {
    return [...arr.slice(0, start), ...items, ...arr.slice(start)];
};

export const getWordCount = (string) => {
    if (!string) return 0;
    const trimmed = string.trim();
    if (!trimmed.length) return 0;
    return trimmed.split(' ').length;
};

export const getHTMLWordCount = (html) => {
    function stripHtmlTagsWithNewlines(input) {
        const replaced = input.replace(/<\/?(p|div|br|h[1-6]|li|ul|ol|table|tr|td|th)[^>]*>/gi, '\n');
        const stripped = replaced.replace(/<\/?[^>]+(>|$)/g, '');
        return stripped.replace(/\n\s*\n/g, '\n').trim();
    }

    const clean = stripHtmlTagsWithNewlines(html);
    if (!clean.length) return 0;
    return clean.replaceAll('\n', ' ').split(/\s+/).length;
};

// given a value, return true if the value is a natural number
// natural numbers are non-negative integers
export const isNatural = (value) => {
    const parsed = parseInt(value);
    return !isNaN(parsed) && Number.isInteger(parsed) && parsed > -1;
};

export const getDuplicateTitle = (originalTitle) => {
    return `Copy of ${originalTitle}`;
};

export const getChatCompletion = async (url, body) => {
    const token = localStorage.getItem('auth_token');
    if (!token) throw new Error();
    return await fetch(url, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body,
    });
};

export const getAIPreview = async ({
    owner_account_id,
    assignment_id,
    target,
    description,
    rubric,
    baselineAssignment,
    baselineCriteria,
    course_id,
    content_bank_resources,
    structure,
    is_rag_ready,
    numSuggestions,
    suggestionLength,
    languageLevel,
    tone,
    toneDescription,
    customInstructions,
    feedbackCustomContext,
    gradingSettings,
    gradingCustomContext,
    submission,
}) => {
    let URL = AI_FEEDBACKS_URL;
    if (target === 'grading') {
        URL = AI_GRADE_URL;
    } else if (target === 'both') {
        URL = AI_REGENERATE_URL;
    } else if (target === 'feedback') {
        URL = AI_FEEDBACKS_URL;
    }
    const assignmentData = JSON.stringify({
        target,
        assignment_id: assignment_id,
        course_id: course_id,
        description: stripHTML(description),
        rubric,
        baselineAssignment: stripHTML(baselineAssignment),
        baselineCriteria,
        is_rag_ready,
        structure,
        numSuggestions,
        suggestionLength,
        languageLevel,
        tone,
        toneDescription,
        customInstructions: stripHTML(customInstructions),
        feedbackCustomContext: stripHTML(feedbackCustomContext),
        gradingSettings,
        gradingCustomContext: stripHTML(gradingCustomContext),
        content_bank_resources,
        script: 'image',
        file: submission,
        platform: process.env.REACT_APP_API_ENDPOINT.split('/').slice(-1),
    });
    const blob = new Blob([assignmentData], { type: 'application/json' });

    const fileName = 'AI_PREVIEW.json'; // Specify your desired filename
    const JSONFile = new File([blob], fileName, { type: 'application/json' });

    const file = await new Promise(async (resolve, reject) => {
        await uploadFile(
            JSONFile,
            () => {},
            (data) => {
                resolve(data);
            },
            (error) => {
                console.error(error);
                reject(error);
            },
            owner_account_id
        );
    });

    return await getChatCompletion(URL, JSON.stringify(file));
};

export const getRubric = async ({
    title,
    description,
    numCriteria,
    useCustomCriteria,
    criteria,
    maxScore,
    numOptions,
}) => {
    return await getChatCompletion(
        AI_RUBRICS_URL,
        JSON.stringify({
            title,
            description: stripHTML(description),
            numCriteria: Number(numCriteria),
            useCustomCriteria,
            criteria,
            maxScore: Number(maxScore),
            numOptions: Number(numOptions),
        })
    );
};

export const getSubmissionGrade = (assignment, submission) => {
    if (!submission.grading) return 0;
    const score = Object.entries(submission.grading.criteria).reduce((acc, [criterionId, optionId]) => {
        const { rate } = assignment.rubric.criteria
            .find(({ id }) => id === criterionId)
            .options.find(({ id }) => id === optionId);
        return acc + Number(rate);
    }, 0);
    return score;
};

export const isAssignmentValid = (assignment) => {
    const isTitleValid = () => {
        return Boolean(assignment.title.trim());
    };

    const isDescriptionValid = () => {
        return Boolean(assignment.description.trim());
    };

    const isRubricValid = () => {
        if (!Boolean(assignment.rubric?.title.trim())) return false;
        return assignment.rubric.criteria.every((criterion) => {
            return criterion.options.every((option) => {
                return isNatural(option.rate);
            });
        });
    };

    const isBaselineValid = () => {
        if (!assignment.baseline) return true;
        return (
            Boolean(assignment.baseline.assignment.trim()) &&
            assignment.rubric.criteria.every((criterion) => {
                return (
                    Object.keys(assignment.baseline.criteria).includes(criterion.id) &&
                    criterion.options.map(({ id }) => id).includes(assignment.baseline.criteria[criterion.id])
                );
            })
        );
    };

    return isTitleValid() && isDescriptionValid() && isRubricValid() && isBaselineValid();
};

export const getToneDescription = (tone) => {
    switch (tone) {
        case 'very-supportive':
            return 'Feedback is overwhelmingly positive and focuses primarily on strengths, offering minimal critique or suggestions for improvement.';
        case 'supportive':
            return 'Feedback is generally positive, highlighting strengths while gently pointing out areas for improvement in a supportive manner.';
        case 'critical':
            return 'Feedback is straightforward and analytical, pinpointing mistakes and areas needing improvement directly. It emphasizes the importance of addressing these areas to facilitate growth and development.';
        case 'balanced':
        default:
            return 'Feedback strikes a balance between positive reinforcement and constructive criticism, providing both praise and suggestions for improvement in equal measure.';
    }
};

export const parseFeedback = ({ commentary, improvement_suggestions }) => {
    let parsed = '';
    parsed += `<p>${commentary.replaceAll('\n', '</p><p>')}</p>`;
    if (improvement_suggestions?.length) {
        parsed += `<p></p>`;
        parsed += `<ol>${improvement_suggestions
            .map(({ summary, elaboration }) => {
                return `<li><p>${summary}</p><ul>${elaboration
                    .map((elaboration) => `<li><p>${elaboration}</p></li>`)
                    .join('')}</ul></li>`;
            })
            .join('')}</ol>`;
    }
    return parsed;
};

export const exportSubmissionsToCSV = (course, assignment, submissions) => {
    const gradedSubmissions = submissions.filter(({ grading }) => grading);
    const totalPoints = assignment.rubric.criteria.reduce((acc, cur) => {
        return acc + Math.max(...cur.options.map(({ rate }) => Number(rate)));
    }, 0);

    let suggestedAverageTotalPoints = '';
    try {
        suggestedAverageTotalPoints = gradedSubmissions.length
            ? gradedSubmissions.reduce((acc, cur) => {
                  const totalPoints = Object.entries(cur.ai.grading).reduce((acc, [criterionId, { optionId }]) => {
                      return (
                          acc +
                          Number(
                              assignment.rubric.criteria
                                  .find(({ id }) => id === criterionId)
                                  .options.find(({ id }) => id === optionId).rate
                          )
                      );
                  }, 0);
                  return acc + totalPoints;
              }, 0) / gradedSubmissions.length
            : '-';
    } catch (e) {
        suggestedAverageTotalPoints = 'N/A';
    }

    let actualAverageTotalPoints = '';
    try {
        if (gradedSubmissions.some(({ grading }) => isEmpty(grading.criteria))) throw new Error();
        actualAverageTotalPoints = gradedSubmissions.length
            ? gradedSubmissions.reduce((acc, cur) => {
                  const totalPoints = Object.entries(cur.grading.criteria).reduce((acc, [criterionId, optionId]) => {
                      return (
                          acc +
                          Number(
                              assignment.rubric.criteria
                                  .find(({ id }) => id === criterionId)
                                  .options.find(({ id }) => id === optionId).rate
                          )
                      );
                  }, 0);
                  return acc + totalPoints;
              }, 0) / gradedSubmissions.length
            : '-';
    } catch (e) {
        actualAverageTotalPoints = 'N/A';
    }

    const csv = [
        [
            'File Name',
            'Final Suggested Grade',
            ...assignment.rubric.criteria.reduce((acc, { id, title, options }, index) => {
                acc.push(`Suggested Grade Criterion ${index + 1}: ${title}`);
                acc.push(`Suggested Rating Criterion ${index + 1}: ${title}`);
                return acc;
            }, []),
            'Final Grade',
            ...assignment.rubric.criteria.reduce((acc, { id, title, options }, index) => {
                acc.push(`Criterion ${index + 1}: ${title}`);
                acc.push(`Criterion ${index + 1} Rating: ${title}`);
                return acc;
            }, []),
            'Feedback',
        ],
        [
            'Points Possible',
            `${totalPoints}`,
            ...assignment.rubric.criteria.reduce((acc, { options }) => {
                acc.push(Math.max(...options.map(({ rate }) => rate)).toString());
                acc.push('(read only)');
                return acc;
            }, []),
            `${totalPoints}`,
            ...assignment.rubric.criteria.reduce((acc, { options }) => {
                acc.push(Math.max(...options.map(({ rate }) => rate)).toString());
                acc.push('(read only)');
                return acc;
            }, []),
            '(read only)',
        ],
        [
            'Class Average',
            `${
                typeof suggestedAverageTotalPoints === 'number'
                    ? Number(suggestedAverageTotalPoints.toFixed(3))
                    : suggestedAverageTotalPoints
            }`,
            ...assignment.rubric.criteria.reduce((acc, { id: criterionId, options }) => {
                try {
                    const criterionTotal = gradedSubmissions.reduce((acc, cur) => {
                        const optionId = cur.ai.grading[criterionId].optionId;
                        return acc + Number(options.find(({ id }) => id === optionId).rate);
                    }, 0);
                    acc.push(
                        gradedSubmissions.length
                            ? `${Number((criterionTotal / gradedSubmissions.length).toFixed(3))}`
                            : 0
                    );
                    acc.push('(read only)');
                    return acc;
                } catch (e) {
                    acc.push('N/A');
                    acc.push('N/A');
                    return acc;
                }
            }, []),
            `${
                typeof actualAverageTotalPoints === 'number'
                    ? Number(actualAverageTotalPoints.toFixed(3))
                    : actualAverageTotalPoints
            }`,
            ...assignment.rubric.criteria.reduce((acc, { id: criterionId, options }) => {
                try {
                    const criterionTotal = gradedSubmissions.reduce((acc, cur) => {
                        const optionId = cur.grading.criteria[criterionId];
                        return acc + Number(options.find(({ id }) => id === optionId).rate);
                    }, 0);
                    acc.push(
                        gradedSubmissions.length
                            ? `${Number((criterionTotal / gradedSubmissions.length).toFixed(3))}`
                            : 0
                    );
                    acc.push('(read only)');
                    return acc;
                } catch (e) {
                    acc.push('N/A');
                    acc.push('N/A');
                    return acc;
                }
            }, []),
            '(read only)',
        ],
        ...gradedSubmissions.map((submission) => {
            let totalSuggestedPoints = '';
            try {
                totalSuggestedPoints = Object.entries(submission.ai.grading).reduce(
                    (acc, [criterionId, { optionId }]) => {
                        return (
                            acc +
                            Number(
                                assignment.rubric.criteria
                                    .find(({ id }) => id === criterionId)
                                    .options.find(({ id }) => id === optionId).rate
                            )
                        );
                    },
                    0
                );
            } catch (e) {
                totalSuggestedPoints = 'N/A';
            }

            let totalActualPoints = '';
            try {
                if (isEmpty(submission.grading.criteria)) throw new Error();
                totalActualPoints = Object.entries(submission.grading.criteria).reduce(
                    (acc, [criterionId, optionId]) => {
                        return (
                            acc +
                            Number(
                                assignment.rubric.criteria
                                    .find(({ id }) => id === criterionId)
                                    .options.find(({ id }) => id === optionId).rate
                            )
                        );
                    },
                    0
                );
            } catch (e) {
                totalActualPoints = 'N/A';
            }

            let feedback = '';
            if (typeof submission.grading.feedback === 'string') {
                feedback = stripHTML(submission.grading.feedback);
            } else if (submission.grading.feedback?.feedback_type === 'templated') {
                feedback = stripHTML(submission.grading.feedback.commentary);
            } else {
                feedback += stripHTML(submission.grading.feedback.commentary);
                submission.grading.feedback.improvement_suggestions
                    .filter(({ active }) => active)
                    .forEach(({ text }) => {
                        feedback += '\n\n';
                        feedback += stripHTML(text);
                    });
            }

            return [
                `${submission.data.original_file ? submission.data.original_file.name : submission.data.name}`,
                `${totalSuggestedPoints}`,
                ...assignment.rubric.criteria.reduce((acc, { id, options }) => {
                    try {
                        const suggestedOptionId = submission.ai.grading[id].optionId;
                        acc.push(Number(options.find(({ id }) => id === suggestedOptionId).rate).toString());
                        acc.push(options.find(({ id }) => id === suggestedOptionId).title);
                        return acc;
                    } catch (e) {
                        acc.push('N/A');
                        return acc;
                    }
                }, []),
                `${totalActualPoints}`,
                ...assignment.rubric.criteria.reduce((acc, { id, options }) => {
                    try {
                        const optionId = submission.grading.criteria[id];
                        acc.push(Number(options.find(({ id }) => id === optionId).rate).toString());
                        acc.push(options.find(({ id }) => id === optionId).title);
                        return acc;
                    } catch (e) {
                        acc.push('N/A');
                        return acc;
                    }
                }, []),
                `${feedback}`,
            ];
        }),
    ];

    downloadCSV(`${course.name}-${assignment.title}.csv`.toLowerCase().replaceAll(' ', '_'), csv);
};

export const exportRubricToCSV = (rubric) => {
    const rubricTotalPoints = rubric.criteria.reduce((acc, cur) => {
        const criterionTotalPoints = Math.max(...cur.options.map(({ rate }) => Number(rate)));
        return acc + criterionTotalPoints;
    }, 0);
    const csv = [
        [rubric.title],
        ['Criteria', 'Points', 'Ratings'],
        ...rubric.criteria
            .map((criterion, index) => {
                const criterionTotalPoints = Math.max(...criterion.options.map(({ rate }) => Number(rate)));
                return [
                    ...(index ? [[]] : []),
                    [
                        criterion.title,
                        `${criterionTotalPoints} pts`,
                        ...criterion.options.map((option) => {
                            return `(${option.rate} pts) ${option.title}`;
                        }),
                    ],
                    [
                        '',
                        '',
                        ...criterion.options.map((option) => {
                            return option.description;
                        }),
                    ],
                ];
            })
            .flat(),
        ['Total Points', `${rubricTotalPoints} pts`],
    ];
    downloadCSV(`${rubric.title}.csv`.toLowerCase().replaceAll(' ', '_'), csv);
};

export const getTemplatedFeedback = (assignment, form) => {
    let f = '';
    assignment.rubric.criteria.forEach(({ id, title }, index) => {
        const optionId = form.criteria[id];
        if (!optionId) return;
        const feedback = assignment.ai_feedback_settings.templated[id]?.[optionId];
        f += `C${index + 1}: ${title}\n`;
        f += feedback;
        if (index !== assignment.rubric.criteria.length - 1) f += `\n\n`;
    });
    return f;
};

export const getPrettyFileTypes = (fileTypes) => {
    const map = {
        'application/pdf': '.pdf',
        'application/msword': '.doc',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
        'application/vnd.ms-excel': '.xls',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
        'application/vnd.ms-powerpoint': '.ppt',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation': '.pptx',
        'text/csv': '.csv',
    };

    return fileTypes.reduce((acc, cur, index) => {
        if (index === 0) return map[cur];
        if (index === fileTypes.length - 1) return `${acc} and ${map[cur]}`;
        return `${acc}, ${map[cur]}`;
    }, '');
};

// default using js built-in in comparator for numeric and string values
export const commonDescendingComparator = (a, b, getter) => {
    const valueA = getter(a);
    const valueB = getter(b);
    if (valueB < valueA) {
        return -1;
    }
    if (valueB > valueA) {
        return 1;
    }
    return 0;
};

export const momentDescendingComparator = (a, b, getter) => {
    const dateA = getter(a);
    const dateB = getter(b);

    // Handle if dates do not exist:
    // Both null -> equal
    if (!dateA && !dateB) {
        return 0;
    }
    // A is null, B is not -> B is greater, so B should come first
    if (!dateA && dateB) {
        return 1;
    }
    // B is null, A is not -> A is greater, so A should come first
    if (dateA && !dateB) {
        return -1;
    }

    // Now both are not null, verify they are valid moment objects
    if (!moment.isMoment(dateA) || !moment.isMoment(dateB)) {
        throw new Error('Getter must return a valid moment.js object or null/undefined.');
    }

    // Compare actual dates in descending order
    if (dateB.isBefore(dateA)) {
        return -1;
    }
    if (dateB.isAfter(dateA)) {
        return 1;
    }
    return 0;
};

export const canAccessFeature = (feature, user, customer, entitlements) => {
    if (user.has_full_access) return true;
    // if (customer === null || customer?.metadata?.is_institutional) return true;
    return entitlements.some(({ lookup_key }) => lookup_key === feature);
};

export const getCardSvg = (brand) => {
    return {
        amex: AmexSvg,
        mastercard: MastercardSvg,
        visa: VisaSvg,
    }[brand];
};
